// SAB-428 + SAB-433: Section D — "Sabotage cards" showcase.
//
// SAB-540: this public marketing section renders from a hardcoded curated set
// (option a) — it NO LONGER queries the operational nfl_sabotage_cards table.
// Rationale: the landing page is the highest-traffic public surface and must
// not depend on a live prod DB read at render time (no round-trip, no coupling
// to prod availability/RLS, no anon-SELECT requirement on an operational table).
//
// The six values below are the curated marketing hero copy, captured from the
// live catalog (staging == prod, verified byte-identical) at the time of the
// scrub. They are FIXED marketing content, not a live mirror — if the store
// catalog's names/rarities/descriptions change, re-sync this array by hand.
// `cost` is the in-app SP draft cost (not rendered here); the visible USD store
// prices ($2/$5/$10) live in the section subhead below.
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';

type Rarity = 'common' | 'rare' | 'legendary';

interface FeaturedCard {
  name: string;
  rarity: Rarity;
  cost: number;
  description: string;
}

// Curated order matches the design — visual rarity rhythm
// (rare, legendary, common, legendary, common, legendary).
// `as const satisfies` validates each element's shape/rarity at build time
// while preserving the tuple length for the drift guard below.
const FEATURED_CARDS = [
  {
    name: 'Pick Six Party',
    rarity: 'rare',
    cost: 25,
    description: "Double the trash points from opponent's QB interceptions.",
  },
  {
    name: 'Snake Reversal',
    rarity: 'legendary',
    cost: 50,
    description: 'Reverse the snake draft order for one round. Pick 1 becomes Pick 12. Round 5+ only.',
  },
  {
    name: 'Iced the Kicker',
    rarity: 'common',
    cost: 10,
    description: "2x multiplier on opponent's kicker missed kicks trash.",
  },
  {
    name: 'Commissioner Scandal',
    rarity: 'legendary',
    cost: 50,
    description: 'ALL multipliers in the league are DOUBLED this week.',
  },
  {
    name: 'Butterfingers',
    rarity: 'common',
    cost: 10,
    description: "1.5x multiplier on opponent's RB fumble trash points.",
  },
  {
    name: 'Roughing the Drafter',
    rarity: 'legendary',
    cost: 50,
    description: 'ALL opponents draft in Guess Who mode for one full round. League-wide chaos.',
  },
] as const satisfies readonly FeaturedCard[];

// Drift guard: the design expects exactly six curated hero cards. `as const`
// above preserves the literal tuple length, so this line fails the build if a
// card is accidentally added or removed. (Value drift vs the DB is intentional
// and manual; see the file header.)
const _featuredCount: 6 = FEATURED_CARDS.length;
void _featuredCount;

const RARITY_STYLES: Record<Rarity, {
  topBorder: string;
  ringBorder: string;
  glow: string;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  label: string;
}> = {
  common: {
    topBorder: 'border-t-[3px] border-t-[hsl(var(--rarity-common))]',
    ringBorder: 'border border-border',
    glow: '',
    badgeBg: 'bg-muted',
    badgeText: 'text-[hsl(var(--rarity-common))]',
    badgeBorder: 'border-border',
    label: 'COMMON',
  },
  rare: {
    topBorder: 'border-t-[3px] border-t-[hsl(var(--rarity-rare))]',
    ringBorder: 'border border-[hsl(220_90%_56%_/_0.45)]',
    glow: 'shadow-[0_0_24px_hsl(220_90%_56%_/_0.14)]',
    badgeBg: 'bg-[hsl(220_90%_56%_/_0.15)]',
    badgeText: 'text-[hsl(var(--rarity-rare))]',
    badgeBorder: 'border-[hsl(220_90%_56%_/_0.4)]',
    label: 'RARE',
  },
  legendary: {
    topBorder: 'border-t-[3px] border-t-[hsl(var(--rarity-legendary))]',
    ringBorder: 'border border-[hsl(280_85%_60%_/_0.5)]',
    glow: 'shadow-[0_0_28px_hsl(280_85%_60%_/_0.2)]',
    badgeBg: 'bg-[hsl(280_85%_60%_/_0.15)]',
    badgeText: 'text-[hsl(var(--rarity-legendary))]',
    badgeBorder: 'border-[hsl(280_85%_60%_/_0.45)]',
    label: 'LEGENDARY',
  },
};

export function SabotageCardsShowcase() {
  return (
    <section id="cards" className="border-t border-border bg-gradient-dark">
      {/* SAB-445: section padding 14→10 on mobile. */}
      <div className="mx-auto max-w-[1180px] px-6 py-10 sm:py-14">
        {/* Header row. SAB-540: the internal "live from nfl_sabotage_cards"
            debug badge was removed — it exposed a table name + deployment
            jargon on the public marketing page. */}
        <div className="mb-6 sm:mb-8">
          <div>
            <div className="font-display text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground mb-3">
              Sabotage cards
            </div>
            <h2
              className="m-0 mb-2.5 font-display font-extrabold"
              style={{ fontSize: 'clamp(1.9rem, 4vw, 2.6rem)' }}
            >
              The signature mechanic.
            </h2>
            <p className="text-[17px] text-muted-foreground max-w-[560px] m-0">
              Buy specific sabotage cards at posted prices, then drop them on your opponent
              to wreck their week. Common $2, Rare $5, Legendary $10. Rarer cards, bigger chaos.
            </p>
          </div>
        </div>

        {/* SAB-540: the old interactive demo was removed for compliance. The
            store showcase is the curated card grid below; this is its
            store-framed lead-in (desktop only — mobile flows straight from the
            header). */}
        <p className="hidden sm:block text-[13px] uppercase tracking-[0.08em] font-display font-bold text-muted-foreground text-center mb-6">
          The store: buy a card, drop it on a rival
        </p>

        {/* Desktop grid — SAB-447: gated to md+ only. Same SAB-445 styling. */}
        <div
          className="hidden sm:grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]"
        >
          {FEATURED_CARDS.map((card) => {
            const s = RARITY_STYLES[card.rarity];
            return (
              <div
                key={card.name}
                className={`bg-card rounded-xl p-[22px] ${s.topBorder} ${s.ringBorder} ${s.glow}`}
              >
                <span
                  data-compact
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-display font-bold uppercase tracking-[0.06em] border ${s.badgeBg} ${s.badgeText} ${s.badgeBorder}`}
                >
                  {s.label}
                </span>
                <h3 className="font-display font-extrabold text-[20px] text-foreground mt-3.5 mb-2">
                  {card.name}
                </h3>
                <p className="text-sm text-muted-foreground m-0">
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* SAB-447: mobile = horizontal swipe carousel (scroll-snap), all 6
            curated cards rendered. Each card is ~85% viewport-wide so the next
            one peeks into view, cuing the swipe. */}
        <div
          className="sm:hidden -mx-6 px-6 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar"
          style={{ scrollPaddingInline: '1.5rem' }}
        >
          {FEATURED_CARDS.map((card) => {
            const s = RARITY_STYLES[card.rarity];
            return (
              <div
                key={card.name}
                className={`flex-none w-[80%] max-w-[300px] bg-card rounded-xl p-4 snap-start ${s.topBorder} ${s.ringBorder} ${s.glow}`}
              >
                <span
                  data-compact
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-display font-bold uppercase tracking-[0.06em] border ${s.badgeBg} ${s.badgeText} ${s.badgeBorder}`}
                >
                  {s.label}
                </span>
                <h3 className="font-display font-extrabold text-[18px] text-foreground mt-3 mb-1.5">
                  {card.name}
                </h3>
                <p className="text-[13px] text-muted-foreground m-0">
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* SAB-540: store CTA replaces the removed demo's call-to-action.
            Reuses the store event so homepage store intent stays tracked. */}
        <div className="mt-8 sm:mt-10 text-center">
          <Button asChild variant="default" className="h-[52px] px-10 text-[17px]">
            <Link
              to="/nfl/leagues/browse"
              onClick={() =>
                trackEvent('homepage_store_cta_click', { source: 'homepage', placement: 'sabotage_cards' })
              }
            >
              Shop Sabotages →
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

export default SabotageCardsShowcase;
